"use client";

import { useState } from "react";
import { Copy, Check, Share2 } from "lucide-react";
import { useT } from "@/lib/use-i18n";

interface ArticleShareBarProps {
  title: string;
}

export function ArticleShareBar({ title }: ArticleShareBarProps) {
  const t = useT();
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback if clipboard API not available
    }
  }

  function handleShareX() {
    if (typeof window === "undefined") return;
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(`${title} via @VortixyOficial`);
    window.open(
      `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      "_blank",
      "noopener,noreferrer",
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-panel)] border border-border/70 bg-card/60 p-4 shadow-xs backdrop-blur-xs">
      <div className="flex items-center gap-2 text-xs font-semibold tracking-wide text-foreground">
        <Share2 className="size-3.5 text-[var(--accent)]" aria-hidden="true" />
        <span>{t("chatShareLink")}</span>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-all hover:bg-muted hover:border-border/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] active:scale-95"
          aria-label={t("chatShareLink")}
        >
          {copied ? (
            <>
              <Check
                className="size-3 text-[var(--success-text)]"
                aria-hidden="true"
              />
              <span className="text-[var(--success-text)] font-semibold">
                {t("chatChatCopied")}
              </span>
            </>
          ) : (
            <>
              <Copy
                className="size-3 text-muted-foreground"
                aria-hidden="true"
              />
              <span>{t("chatChatCopy")}</span>
            </>
          )}
        </button>

        <button
          type="button"
          onClick={handleShareX}
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-all hover:bg-muted hover:border-border/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] active:scale-95"
          aria-label={t("blogShareOnX")}
        >
          <svg
            className="size-3 fill-currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
          <span>X</span>
        </button>
      </div>
    </div>
  );
}
